import { allCommands } from "app/client/components/commands";
import { makeT } from "app/client/lib/localization";
import { inlineMarkdown, markdown } from "app/client/lib/markdown";
import { AppModel } from "app/client/models/AppModel";
import { urlState } from "app/client/models/gristUrlState";
import { bigPrimaryButton, cssButton } from "app/client/ui2018/buttons";
import { labeledSquareCheckbox } from "app/client/ui2018/checkbox";
import { cssLink, cssNestedLinks } from "app/client/ui2018/links";
import { cssModalBody,
  cssModalButtons,
  cssModalSubheading,
  cssModalTitle,
  cssModalWidth,
  IModalControl,
  modal,
} from "app/client/ui2018/modals";
import { commonUrls, isFeatureEnabled } from "app/common/gristUrls";
import { tokens } from "app/common/ThemePrefs";

import { Computed, dom, makeTestId, Observable, styled } from "grainjs";

const t = makeT("OpenAccessibilityModal");

const testId = makeTestId("test-accessibility-modal-");

/**
 * Opens a modal containing accessibility options.
 */
export function openAccessibilityModal(appObs: Observable<AppModel | null>) {
  const showHighContrastTheme = isFeatureEnabled("themes");

  return modal(
    (ctl) => {
      return [
        cssModalWidth("fixed-wide"),
        cssModalTitle(
          t(`Accessibility`),
          { "role": "heading", "aria-level": 1 },
          testId("title"),
        ),
        cssModalBody(
          showHighContrastTheme ? highContrastThemeSection(appObs, ctl) : null,
          keyboardSection(),
          screenReaderSection(appObs),
        ),
        cssModalButtons(
          bigPrimaryButton(t(`Close`),
            dom.on("click", () => ctl.close()),
            testId("confirm"),
          ),
        ),
      ];
    },
  );
}

const highContrastThemeSection = (appObs: Observable<AppModel | null>, ctl: IModalControl) => {
  const themePrefs = appObs.get()?.themePrefs;
  return cssSection(
    cssModalSubheading(t("High contrast theme"), { "role": "heading", "aria-level": 2 }),
    dom.domComputed((use) => {
      const currentTheme = themePrefs ? use(themePrefs).colors.light : undefined;
      const isHighContrast = currentTheme === "HighContrastLight";
      if (isHighContrast) {
        return dom("p",
          t("You are currently using the high contrast theme."),
          { id: "a11y-modal-high-contrast-theme-enabled", tabindex: -1 },
        );
      }
      return dom("div",
        markdown(t("You are currently **not using** the high contrast theme.")),
        dom("p", cssAlwaysReadableButton(
          // Note: default light theme has contrast issues.
          // It would be weird to have this button, that is made to easily switch to a contrasted theme,
          // being itself not readable enough.
          // So we make sure this button is always correctly readable, whatever the theme.
          // Note: we use a distinct class name to make css overrides easier. A light theme changed with CSS overrides
          // might not have the contrast issues. To allow easier changes of this specific behavior in CSS overrides,
          // the class is not generated by grainjs.
          dom.cls("force-contrasts"),
          t("Use the high contrast theme (light appearance)"),
          dom.on("click", () => {
            themePrefs?.set({
              ...themePrefs.get(),
              colors: { light: "HighContrastLight", dark: "HighContrastLight" },
              appearance: "light",
              syncWithOS: false,
            });
            // after clicking the button, it disappears. Focus the text element above that confirms the change.
            setTimeout(() => {
              const element = document.getElementById("a11y-modal-high-contrast-theme-enabled");
              if (element) {
                element.focus();
              }
            }, 100);
          }),
          testId("high-contrast-theme-button"),
        )),
      );
    }),
    dom("p", t("To see other available themes, go to your {{profileSettingsLink}}.", {
      profileSettingsLink: cssLink(
        // close the modal when clicking the profile settings link, in case we already are on the profile settings page.
        dom.on("click", () => ctl.close()),
        urlState().setLinkUrl({ account: "account" }),
        t("profile settings"),
      ),
    })),
  );
};

const keyboardSection = () => {
  const nextRegionShortcut = dom("span", getCssKeys(allCommands.nextRegion.humanKeys));
  const prevRegionShortcut = dom("span", getCssKeys(allCommands.prevRegion.humanKeys));
  const creatorPanelShortcut = dom("span", getCssKeys(allCommands.creatorPanel.humanKeys));
  const nextJumpTargetShortcut = dom("span", getCssKeys(allCommands.nextJumpTarget.humanKeys));
  const shortcutsModal = dom("span", getCssKeys(allCommands.shortcuts.humanKeys));
  const accessibilityModal = dom("span", getCssKeys(allCommands.accessibility.humanKeys));
  return cssSection(
    cssModalSubheading(t("Keyboard navigation"), { "role": "heading", "aria-level": 2 }),
    dom("p", t("On a document page, keyboard navigation is first locked on the current widget.")),
    dom("p", t("Focus on other parts of the user interface using the following shortcuts:")),
    cssShortcutList(
      cssShortcutRow(t("{{nextRegionShortcut}} Focus on the next region", { nextRegionShortcut })),
      cssShortcutRow(t("{{prevRegionShortcut}} Focus on the previous region", { prevRegionShortcut })),
      cssShortcutRow(t("{{creatorPanelShortcut}} Focus to and from the creator panel", { creatorPanelShortcut })),
    ),
    dom("p", t("\"Regions\" are what we call the different parts of the user interface:")),
    dom("ul",
      dom("li", t("The left panel, home of the main navigation.")),
      dom("li", t("The top panel, or the document header.")),
      dom("li", inlineMarkdown(t(
        "On document pages, each [widget]({{supportPageUrl}}) is a region that can receive focus.",
        { supportPageUrl: commonUrls.helpWidgets },
      ))),
      dom("li", t("On non-document pages, the main content area is a region.")),
      dom("li", t("Finally, the right panel – or the creator panel – is only available \
through its own shortcut and is not included in the next and previous region cycle.")),
    ),
    dom("p", t("When focus is on a widget, {{nextJumpTargetShortcut}} toggles focus between the widget and its \
header.", { nextJumpTargetShortcut })),
    dom("p", t("When focus is on a panel, the same shortcut lets you jump between predefined landmarks, making \
it easier to quickly navigate inside that panel.")),
    cssModalSubheading(t("Other important keyboard shortcuts"), { "role": "heading", "aria-level": 3 }),
    cssShortcutList(
      cssShortcutRow(t("{{shortcutsModal}} Show the complete list of keyboard shortcuts", { shortcutsModal })),
      cssShortcutRow(t("{{accessibilityModal}} Show the accessibility options (this modal)", { accessibilityModal })),
    ),
  );
};

const screenReaderSection = (appObs: Observable<AppModel | null>) => {
  const appModel = appObs.get();
  const screenReaderMode = appModel?.screenReaderMode;
  const checked = Computed.create(null,
    use => screenReaderMode ? use(screenReaderMode) : false,
  ).onWrite(val => screenReaderMode?.set(val));

  return cssSection(
    cssModalSubheading(t("Screen reader navigation"), { "role": "heading", "aria-level": 2 }),
    dom("p", t("Please first read the \"Keyboard navigation\" section above to understand how to navigate \
across the interface's regions: specific keyboard shortcuts need to be used.")),
    dom("p", t("We recommend using Firefox, Chrome or Edge in order to use Grist with a screen reader.")),
    dom("p", t("Use your screen reader's focus mode, or disable Quick Nav if you use VoiceOver.")),
    dom("p", t("While not mandatory, you can enable screen reader improvements for a better \
navigation experience:")),
    dom("p",
      labeledSquareCheckbox(
        checked,
        t("Enable screen reader improvements (keyboard shortcut: {{shortcut}})", {
          shortcut: allCommands.toggleScreenReaderMode.humanKeys,
        }),
        testId("screen-reader-checkbox"),
      ),
      dom.autoDispose(checked),
    ),
    dom("p", t("Warning: screen reader support is a work-in-progress in Grist and is improving over time. \
Feedback is greatly appreciated!"),
    ),
  );
};

const getCssKeys = (keys: string[]) => {
  return keys.map((k, i) => i === keys.length - 1 ? cssKey(k) : [cssKey(k), t(" or ")]);
};

const cssSection = styled(cssNestedLinks, `
  margin-bottom: 32px;
  &:last-child {
    margin-bottom: 0;
  }
`);

const cssKey = styled("span", `
  display: inline-block;
  font-family: ${tokens.fontFamilyData};
  color: ${tokens.body};
  background-color: ${tokens.bgSecondary};
  border: 1px solid ${tokens.decoration};
  border-bottom: 3px solid ${tokens.decoration};
  padding: 0.2em 0.4em;
  border-radius: 0.2em;
  line-height: 1.3;
`);

const cssShortcutList = styled("ul", `
  padding-left: 0;
`);

const cssShortcutRow = styled("li", `
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin-bottom: 0.25rem;

  & > span:first-child {
    text-align: right;
    min-width: 110px;
  }
`);

const cssAlwaysReadableButton = styled(cssButton, `
  html[data-grist-theme="GristLight"] &.force-contrasts {
    color: ${tokens.body};
    border-color: currentColor;
  }
`);
